import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Banknote,
  HardHat,
  Home,
  Link2,
  MinusCircle,
  RefreshCw,
  Scale,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FilterBar } from "@/components/ui/FilterBar";
import type { FilterValues } from "@/components/ui/FilterBar/types";
import type { MultiSelectOption } from "@/components/ui/MultiSelect";
import { useAppAccess } from "@/hooks/useAppAccess";
import { useModules } from "@/context/ModulesContext";
import { fetchAllUnitsForRegistry, type ComercialUnitRow } from "@/services/comercial";
import { fetchBudgetResources } from "@/services/orcamento";
import { listUnidadesConstrutivas, UC_SEM_LABEL } from "@/pages/Orcamento/chartUtils";
import type { OutcomeItem } from "@/services/financeiro";
import {
  gerencialCumulativeFilterValues,
  fetchGerencialData,
  fetchUnitsByCC,
  type UnitsByCCItem,
} from "@/services/gerencial";
import {
  fetchChaoMetrics,
  fetchChaoServiceCatalog,
  fetchChaoUnitAvgValue,
  fetchChaoFinancialDeduction,
  fetchChaoUnitLinks,
  type ChaoPhysicalMonthPoint,
  type ChaoPhysicalWeekPoint,
  type ChaoServiceDef,
  type ChaoUnitMetrics,
} from "@/services/chaoDeObra";
import { EapWeightsModal } from "./EapWeightsModal";
import { ChaoUnitProgressCard } from "./ChaoUnitProgressCard";
import { ChaoObraResumoFaixa } from "./ChaoObraResumoFaixa";
import { ChaoUnitLinksModal } from "./ChaoUnitLinksModal";
import { ChaoValorMedioUnidadeModal } from "./ChaoValorMedioUnidadeModal";
import { ChaoDeducaoFinanceiraModal } from "./ChaoDeducaoFinanceiraModal";
import { ChaoAvancoFisicoPorUcModal } from "./ChaoAvancoFisicoPorUcModal";
import { ChaoAvancoMapaPanel, ChaoAvancoMapaToggle } from "./ChaoAvancoMapaPanel";
import { ChaoProdutividadeView } from "./ChaoProdutividadeView";
import {
  computeValorTotalImoveis,
  sumFinancialDeductionForCostCenters,
  sumGerencialForCostCenters,
} from "./chaoAvancoFinanceiroUtils";
import {
  buildRegistryCountByCc,
  costCenterIdsMatch,
  filterUnitsByCostCenter,
  filterUnitsByConstructiveUnits,
  filterUnitsBySearch,
  resolveConstructiveUnitLabel,
  resolveUnitCountForCostCenters,
  sortUnitsByLastUpdate,
} from "./chaoUnitCardUtils";
import { CHAO_EMPTY_FILTERS } from "./chaoListState";

function chaoListApiError(e: unknown, fallback: string): string {
  const ax = e as {
    response?: { data?: { error?: string; detail?: string }; status?: number };
    code?: string;
    message?: string;
  };
  const apiErr = ax.response?.data?.error;
  if (typeof apiErr === "string" && apiErr.trim()) return apiErr.trim();
  if (ax.code === "ECONNABORTED" || /timeout/i.test(ax.message ?? "")) {
    return "A busca de unidades no Sienge demorou demais. Tente novamente ou selecione menos obras.";
  }
  if (ax.response?.status === 502) {
    return "Unidades indisponíveis no Sienge (erro 502). Tente novamente em instantes.";
  }
  if (e instanceof Error && e.message && !/^Request failed with status code \d+$/i.test(e.message)) {
    return e.message;
  }
  return fallback;
}

async function loadConstructiveUnitOptions(costCenterId: string): Promise<MultiSelectOption[]> {
  try {
    const budget = await fetchBudgetResources(costCenterId);
    const names = listUnidadesConstrutivas(budget, []).filter((n) => n !== UC_SEM_LABEL);
    return names.map((n) => ({ value: n, label: n }));
  } catch {
    return [];
  }
}

export function ChaoDeObraListPage() {
  const { isAdmin, isMaster, can } = useAppAccess();
  const { ccOptions, loadingCC, chaoDeObra, ccNameMap } = useModules();
  const {
    listLoading,
    setListLoading,
    setUnitTransitionLoading,
    listCache,
    persistListCache,
    clearListCache,
  } = chaoDeObra;
  const init = listCache.hasData ? listCache : null;

  const [allUnits, setAllUnits] = useState<ComercialUnitRow[]>(() => init?.allUnits ?? []);
  const [metrics, setMetrics] = useState<Record<string, ChaoUnitMetrics>>(() => init?.metrics ?? {});
  const [error, setError] = useState<string | null>(null);
  const [filterValues, setFilterValues] = useState<FilterValues>(
    () => init?.filterValues ?? CHAO_EMPTY_FILTERS,
  );
  const [appliedCostCenter, setAppliedCostCenter] = useState<string[]>(
    () => init?.appliedCostCenter ?? [],
  );
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [weightsCatalog, setWeightsCatalog] = useState<ChaoServiceDef[]>([]);
  const [weightsLoading, setWeightsLoading] = useState(false);
  const [unitSearch, setUnitSearch] = useState(() => init?.unitSearch ?? "");
  const [enterpriseGeneralPct, setEnterpriseGeneralPct] = useState(
    () => init?.enterpriseGeneralPct ?? 0,
  );
  const [constructiveUnitOptions, setConstructiveUnitOptions] = useState<MultiSelectOption[]>(
    () => init?.constructiveUnitOptions ?? [],
  );
  const [loadingConstructiveUnits, setLoadingConstructiveUnits] = useState(false);
  const [unitLinksByCc, setUnitLinksByCc] = useState<Record<string, Record<string, string>>>(
    () => init?.unitLinksByCc ?? {},
  );
  const [linksModalOpen, setLinksModalOpen] = useState(false);
  const [unitAvgByCc, setUnitAvgByCc] = useState<Record<string, number>>(
    () => init?.unitAvgByCc ?? {},
  );
  const [financialDeductionByCc, setFinancialDeductionByCc] = useState<Record<string, number>>(
    () => init?.financialDeductionByCc ?? {},
  );
  const [avgModalOpen, setAvgModalOpen] = useState(false);
  const [deductionModalOpen, setDeductionModalOpen] = useState(false);
  const [physicalByUcOpen, setPhysicalByUcOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"casas" | "mapa" | "produtividade">("casas");
  const [scheduleRefreshKey, setScheduleRefreshKey] = useState(0);
  const [gerencialOutcome, setGerencialOutcome] = useState<OutcomeItem[] | null>(
    () => init?.gerencialOutcome ?? null,
  );
  const [gerencialLoading, setGerencialLoading] = useState(false);
  const [physicalSeries, setPhysicalSeries] = useState<ChaoPhysicalMonthPoint[]>(
    () => init?.physicalSeries ?? [],
  );
  const [physicalWeekSeries, setPhysicalWeekSeries] = useState<ChaoPhysicalWeekPoint[]>(
    () => init?.physicalWeekSeries ?? [],
  );
  const [unitsByCC, setUnitsByCC] = useState<UnitsByCCItem[]>(() => init?.unitsByCC ?? []);

  const primaryCostCenterId = appliedCostCenter.length === 1 ? appliedCostCenter[0]! : "";

  const filteredUnits = useMemo(() => {
    if (!appliedCostCenter.length) return [];
    return filterUnitsByCostCenter(allUnits, appliedCostCenter);
  }, [allUnits, appliedCostCenter]);

  const unitsInAppliedCcOnly = useMemo(() => {
    if (!appliedCostCenter.length) return [];
    const ccSet = new Set(appliedCostCenter.map((x) => String(x).trim()));
    return allUnits.filter((u) => ccSet.has(String(u.costCenterId).trim()));
  }, [allUnits, appliedCostCenter]);

  const sortedUnits = useMemo(
    () => sortUnitsByLastUpdate(filteredUnits, metrics),
    [filteredUnits, metrics],
  );

  const unitsAfterConstructiveFilter = useMemo(() => {
    const selected = filterValues.constructiveUnit ?? [];
    if (!selected.length) return sortedUnits;
    return filterUnitsByConstructiveUnits(
      sortedUnits,
      appliedCostCenter,
      selected,
      unitLinksByCc,
    );
  }, [sortedUnits, filterValues.constructiveUnit, appliedCostCenter, unitLinksByCc]);

  const displayedUnits = useMemo(
    () => filterUnitsBySearch(unitsAfterConstructiveFilter, unitSearch),
    [unitsAfterConstructiveFilter, unitSearch],
  );

  const searchMatchCount = displayedUnits.length;

  const allCcIds = useMemo(() => ccOptions.map((o) => o.value), [ccOptions]);

  const registryCountByCc = useMemo(
    () => buildRegistryCountByCc(unitsInAppliedCcOnly, appliedCostCenter),
    [unitsInAppliedCcOnly, appliedCostCenter],
  );

  const unitCountByCc = useMemo(() => {
    const m: Record<string, number> = {};
    for (const cc of appliedCostCenter) {
      const official = unitsByCC.find((u) => costCenterIdsMatch(u.costCenterId, cc))?.unitCount;
      const registry = registryCountByCc[cc] ?? 0;
      m[cc] = official != null && official > 0 ? official : registry;
    }
    return m;
  }, [appliedCostCenter, registryCountByCc, unitsByCC]);

  const totalUnitsForObra = useMemo(
    () => resolveUnitCountForCostCenters(appliedCostCenter, registryCountByCc, unitsByCC),
    [appliedCostCenter, registryCountByCc, unitsByCC],
  );

  const avancoFinanceiro = useMemo(() => {
    if (!appliedCostCenter.length) {
      return { gerencialTotal: 0, valorTotal: 0, avgConfigured: false, financialDeduction: 0 };
    }
    const outcome = gerencialOutcome ?? [];
    const gerencialTotal = sumGerencialForCostCenters(outcome, appliedCostCenter);
    const valorTotal = computeValorTotalImoveis(unitCountByCc, unitAvgByCc, appliedCostCenter);
    const avgConfigured = appliedCostCenter.some((cc) => (unitAvgByCc[cc] ?? 0) > 0);
    const financialDeduction = sumFinancialDeductionForCostCenters(
      financialDeductionByCc,
      appliedCostCenter,
    );
    return { gerencialTotal, valorTotal, avgConfigured, financialDeduction };
  }, [appliedCostCenter, gerencialOutcome, unitCountByCc, unitAvgByCc, financialDeductionByCc]);

  const loadAvgForCostCenters = useCallback(
    async (ccIds: string[], base: Record<string, number>) => {
      const next = { ...base };
      const entries = await Promise.all(
        ccIds.map(async (cc) => {
          const res = await fetchChaoUnitAvgValue(cc);
          return [cc, res.success && res.value && res.value > 0 ? res.value : 0] as const;
        }),
      );
      for (const [cc, v] of entries) next[cc] = v;
      setUnitAvgByCc(next);
      return next;
    },
    [],
  );

  const loadLinksForCostCenters = useCallback(
    async (ccIds: string[], base: Record<string, Record<string, string>>) => {
      const next = { ...base };
      const entries = await Promise.all(
        ccIds.map(async (cc) => {
          const res = await fetchChaoUnitLinks(cc);
          return [cc, res.success ? (res.links ?? {}) : {}] as const;
        }),
      );
      for (const [cc, links] of entries) next[cc] = links;
      setUnitLinksByCc(next);
      return next;
    },
    [],
  );

  const loadDeductionForCostCenters = useCallback(
    async (ccIds: string[], base: Record<string, number>) => {
      const next = { ...base };
      const entries = await Promise.all(
        ccIds.map(async (cc) => {
          const res = await fetchChaoFinancialDeduction(cc);
          return [cc, res.success && res.value && res.value > 0 ? res.value : 0] as const;
        }),
      );
      for (const [cc, v] of entries) next[cc] = v;
      setFinancialDeductionByCc(next);
      return next;
    },
    [],
  );

  const loadGerencialForCostCenters = useCallback(
    async (ccIds: string[]): Promise<OutcomeItem[] | null> => {
      setGerencialLoading(true);
      try {
        /* Só selectionType=P: 1 bulk no período acumulado (sem fatias de D → bem mais rápido, evita 429). */
        const data = await fetchGerencialData(gerencialCumulativeFilterValues(ccIds), allCcIds, {
          selectionTypes: ["P"],
        });
        setGerencialOutcome(data.outcome);
        return data.outcome;
      } catch {
        setGerencialOutcome(null);
        return null;
      } finally {
        setGerencialLoading(false);
      }
    },
    [allCcIds],
  );

  const loadDataForCostCenters = useCallback(
    async (
      selectedCc: string[],
      cacheOpts?: { filterValues: FilterValues; unitSearch: string; persist?: boolean },
    ) => {
      const list = await fetchAllUnitsForRegistry();
      setAllUnits(list);
      const rows = filterUnitsByCostCenter(list, selectedCc);
      const ids = rows.map((u) => u.id);

      let metricsData: Record<string, ChaoUnitMetrics> = {};
      let enterprisePct = 0;
      let physical: ChaoPhysicalMonthPoint[] = [];
      let physicalWeeks: ChaoPhysicalWeekPoint[] = [];

      if (ids.length === 0) {
        setMetrics({});
        setEnterpriseGeneralPct(0);
        setPhysicalSeries([]);
        setPhysicalWeekSeries([]);
      } else {
        const m = await fetchChaoMetrics(ids, { historyMonths: 24, historyWeeks: 26 });
        if (m.success) {
          metricsData = m.byUnit ?? {};
          enterprisePct = m.enterpriseGeneralPct ?? 0;
          physical = m.physicalSeries ?? [];
          physicalWeeks = m.physicalWeekSeries ?? [];
          setMetrics(metricsData);
          setEnterpriseGeneralPct(enterprisePct);
          setPhysicalSeries(physical);
          setPhysicalWeekSeries(physicalWeeks);
        } else {
          setMetrics({});
          setEnterpriseGeneralPct(0);
          setPhysicalSeries([]);
          setPhysicalWeekSeries([]);
        }
      }

      const [links, avgs, deductions, unitsByCcData] = await Promise.all([
        loadLinksForCostCenters(selectedCc, unitLinksByCc),
        loadAvgForCostCenters(selectedCc, unitAvgByCc),
        loadDeductionForCostCenters(selectedCc, financialDeductionByCc),
        fetchUnitsByCC(),
      ]);
      setUnitsByCC(unitsByCcData);

      let ucOpts: MultiSelectOption[] = [];
      const primary = selectedCc[0];
      if (primary) {
        setLoadingConstructiveUnits(true);
        try {
          ucOpts = await loadConstructiveUnitOptions(primary);
          setConstructiveUnitOptions(ucOpts);
        } finally {
          setLoadingConstructiveUnits(false);
        }
      } else {
        setConstructiveUnitOptions([]);
      }

      const fv = cacheOpts?.filterValues ?? filterValues;
      const us = cacheOpts?.unitSearch ?? unitSearch;
      const shouldPersist = cacheOpts?.persist !== false;

      /* Persist unidades já; o financeiro segue em background (não trava o overlay). */
      if (shouldPersist) {
        persistListCache({
          hasData: selectedCc.length > 0,
          appliedCostCenter: selectedCc,
          filterValues: fv,
          unitSearch: us,
          allUnits: list,
          metrics: metricsData,
          enterpriseGeneralPct: enterprisePct,
          physicalSeries: physical,
          physicalWeekSeries: physicalWeeks,
          unitLinksByCc: links,
          unitAvgByCc: avgs,
          financialDeductionByCc: deductions,
          constructiveUnitOptions: ucOpts,
          unitsByCC: unitsByCcData,
        });
      }

      void (async () => {
        const outcome = await loadGerencialForCostCenters(selectedCc);
        if (!shouldPersist) return;
        persistListCache({
          gerencialOutcome: outcome,
        });
      })();

      return {
        list,
        metricsData,
        enterprisePct,
        physical,
        links,
        avgs,
        deductions,
        outcome: null as OutcomeItem[] | null,
        ucOpts,
        unitsByCcData,
      };
    },
    [
      filterValues,
      unitSearch,
      unitLinksByCc,
      unitAvgByCc,
      financialDeductionByCc,
      loadLinksForCostCenters,
      loadAvgForCostCenters,
      loadDeductionForCostCenters,
      loadGerencialForCostCenters,
      persistListCache,
    ],
  );

  const applyFilters = useCallback(async () => {
    const selectedCc = [...(filterValues.costCenter ?? [])].map((x) => String(x).trim()).filter(Boolean);
    if (selectedCc.length === 0) {
      setError("Selecione pelo menos um centro de custo e clique em Aplicar.");
      return;
    }
    setError(null);
    setListLoading(true);
    try {
      await loadDataForCostCenters(selectedCc, {
        filterValues,
        unitSearch: "",
        persist: true,
      });
      setAppliedCostCenter(selectedCc);
      setUnitSearch("");
    } catch (e) {
      setError(chaoListApiError(e, "Não foi possível carregar os dados."));
      /* Não zera filtros/seleção: evita voltar ao idle enganoso “Nenhuma unidade carregada”. */
    } finally {
      setListLoading(false);
    }
  }, [filterValues.costCenter, loadDataForCostCenters, setListLoading]);

  const resetFilters = useCallback(() => {
    setListLoading(false);
    clearListCache();
    setFilterValues(CHAO_EMPTY_FILTERS);
    setAppliedCostCenter([]);
    setAllUnits([]);
    setMetrics({});
    setEnterpriseGeneralPct(0);
    setUnitSearch("");
    setConstructiveUnitOptions([]);
    setUnitLinksByCc({});
    setUnitAvgByCc({});
    setGerencialOutcome(null);
    setGerencialLoading(false);
    setPhysicalSeries([]);
    setPhysicalWeekSeries([]);
    setUnitsByCC([]);
    setError(null);
  }, [setListLoading, clearListCache]);

  const reload = useCallback(async () => {
    if (appliedCostCenter.length === 0) {
      setError("Selecione o centro de custo e clique em Aplicar para carregar as unidades.");
      return;
    }
    setError(null);
    setListLoading(true);
    try {
      await loadDataForCostCenters(appliedCostCenter, {
        filterValues,
        unitSearch,
        persist: true,
      });
    } catch (e) {
      setError(chaoListApiError(e, "Não foi possível atualizar os dados."));
    } finally {
      setListLoading(false);
    }
  }, [appliedCostCenter, filterValues, unitSearch, loadDataForCostCenters, setListLoading]);

  /** Ao voltar da unidade: mantém filtros e atualiza métricas em segundo plano. */
  useEffect(() => {
    if (!listCache.hasData || appliedCostCenter.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        await loadDataForCostCenters(appliedCostCenter, {
          filterValues,
          unitSearch,
          persist: true,
        });
      } catch {
        if (!cancelled) {
          setError("Não foi possível atualizar os dados ao retornar.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const idleBeforeApply =
    appliedCostCenter.length === 0 && allUnits.length === 0 && !listLoading && !error;

  const openWeightsModal = useCallback(async () => {
    setWeightsLoading(true);
    setError(null);
    try {
      const catalog = await fetchChaoServiceCatalog();
      if (!catalog.length) {
        setError("Catálogo de serviços indisponível.");
        return;
      }
      setWeightsCatalog(catalog);
      setWeightsOpen(true);
    } catch (e) {
      setError(chaoListApiError(e, "Não foi possível carregar o catálogo."));
    } finally {
      setWeightsLoading(false);
    }
  }, []);

  const onWeightsSaved = useCallback(
    (catalog: ChaoServiceDef[]) => {
      setWeightsCatalog(catalog);
      if (appliedCostCenter.length > 0) void reload();
    },
    [appliedCostCenter.length, reload],
  );

  const openLinksModal = () => {
    if (appliedCostCenter.length !== 1) {
      setError("Selecione exatamente um centro de custo / obra para configurar os vínculos de unidade construtiva.");
      return;
    }
    setLinksModalOpen(true);
  };

  const openAvgModal = () => {
    if (appliedCostCenter.length !== 1) {
      setError(
        "Selecione exatamente um centro de custo / obra para informar o valor médio da unidade de imóvel.",
      );
      return;
    }
    setAvgModalOpen(true);
  };

  const openDeductionModal = () => {
    if (appliedCostCenter.length !== 1) {
      setError(
        "Selecione exatamente um centro de custo / obra para informar a dedução do valor realizado.",
      );
      return;
    }
    setDeductionModalOpen(true);
  };

  const primaryCcLabel =
    (primaryCostCenterId && ccNameMap.get(primaryCostCenterId)) ||
    ccOptions.find((o) => o.value === primaryCostCenterId)?.label ||
    primaryCostCenterId;

  return (
    <div className="flex flex-col gap-6 pb-8 w-full min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <HardHat className="w-5 h-5 text-amber-600" />
            Chão de Obra
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Casas cadastradas no Sienge. Selecione o <strong>centro de custo / obra</strong> e clique em{" "}
            <strong>Aplicar</strong> para carregar as unidades. Registre fotos por ambiente e o avanço físico dos
            serviços.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {appliedCostCenter.length > 0 && (
            <ChaoAvancoMapaToggle mode={viewMode} onChange={setViewMode} />
          )}
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void openWeightsModal()}
              loading={weightsLoading}
              disabled={weightsLoading}
              leftIcon={<Scale className="w-4 h-4" />}
              className="text-slate-600 hover:text-teal-700 hover:bg-teal-50"
              title="Ponderação global dos serviços (todas as obras e unidades)"
            >
              Ponderação
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void reload()}
            loading={listLoading}
            disabled={appliedCostCenter.length === 0 || listLoading}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Atualizar
          </Button>
        </div>
      </div>

      <FilterBar
        values={filterValues}
        onChange={setFilterValues}
        onApply={() => void applyFilters()}
        onReset={resetFilters}
        costCenterOptions={ccOptions}
        constructiveUnitOptions={appliedCostCenter.length > 0 ? constructiveUnitOptions : undefined}
        loadingOptions={loadingCC}
        loadingConstructiveUnitOptions={loadingConstructiveUnits}
        loading={listLoading}
        showCostCenterOnly
        showUnitSearch
        unitSearchLabel="Buscar unidade"
        unitSearch={unitSearch}
        onUnitSearchChange={setUnitSearch}
        unitSearchDisabled={appliedCostCenter.length === 0}
        filterActions={
          <>
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={openAvgModal}
              disabled={appliedCostCenter.length !== 1 || listLoading}
              leftIcon={<Banknote className="w-4 h-4" />}
              title={
                appliedCostCenter.length !== 1
                  ? "Selecione um único centro de custo para o valor médio"
                  : "Valor médio da unidade de imóvel (base do avanço financeiro)"
              }
            >
              Valor médio
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={openDeductionModal}
              disabled={appliedCostCenter.length !== 1 || listLoading}
              leftIcon={<MinusCircle className="w-4 h-4" />}
              title={
                appliedCostCenter.length !== 1
                  ? "Selecione um único centro de custo para a dedução"
                  : "Deduzir valor do realizado (somente neste painel)"
              }
            >
              Dedução
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={openLinksModal}
              disabled={appliedCostCenter.length !== 1 || listLoading}
              leftIcon={<Link2 className="w-4 h-4" />}
              title={
                appliedCostCenter.length !== 1
                  ? "Selecione um único centro de custo para configurar vínculos"
                  : "Vincular unidades de imóvel às unidades construtivas do orçamento"
              }
            >
              Vínculos UC
            </Button>
          </>
        }
      />

      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {idleBeforeApply ? (
        <div className="text-center py-12 px-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50">
          <Home className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-700 font-medium">Nenhuma unidade carregada ainda</p>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            Escolha um ou mais <strong>centros de custo / obra</strong> na faixa de filtros e clique em{" "}
            <strong>Aplicar</strong> para buscar as unidades no Sienge.
          </p>
        </div>
      ) : error && allUnits.length === 0 && !listLoading ? (
        <div className="text-center py-12 px-4 rounded-2xl border border-dashed border-red-200 bg-red-50/40">
          <Home className="w-10 h-10 text-red-300 mx-auto mb-2" />
          <p className="text-slate-800 font-medium">Não foi possível carregar as unidades</p>
          <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
            Clique em <strong>Aplicar</strong> ou <strong>Atualizar</strong> para tentar novamente.
          </p>
        </div>
      ) : listLoading ? null : allUnits.length === 0 ? (
        <div className="text-center py-12 px-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50">
          <Home className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-600 font-medium">Nenhuma unidade retornada pela API</p>
          <p className="text-sm text-slate-500 mt-1">Verifique permissões do endpoint /units no Sienge.</p>
        </div>
      ) : unitsInAppliedCcOnly.length === 0 ? (
        <div className="text-center py-12 px-4 rounded-2xl border border-dashed border-amber-200 bg-amber-50/60">
          <Home className="w-10 h-10 text-amber-300 mx-auto mb-2" />
          <p className="text-slate-800 font-medium">Nenhuma unidade neste centro de custo</p>
          <p className="text-sm text-slate-600 mt-1">
            Ajuste a seleção e clique em <strong>Aplicar</strong> novamente, ou use <strong>Limpar</strong> na faixa de
            filtros.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <ChaoObraResumoFaixa
            generalPct={enterpriseGeneralPct}
            totalUnits={totalUnitsForObra}
            cardsLoaded={filteredUnits.length}
            valorTotalImoveis={avancoFinanceiro.valorTotal}
            gerencialTotal={avancoFinanceiro.gerencialTotal}
            financialDeduction={avancoFinanceiro.financialDeduction}
            avgConfigured={avancoFinanceiro.avgConfigured}
            physicalSeries={physicalSeries}
            physicalWeekSeries={physicalWeekSeries}
            outcome={gerencialOutcome ?? []}
            appliedCostCenter={appliedCostCenter}
            loadingFinancial={gerencialLoading}
            onOpenPhysicalByUc={() => setPhysicalByUcOpen(true)}
            scheduleRefreshKey={scheduleRefreshKey}
          />

          {unitSearch.trim() && (
            <p className="text-xs text-slate-500 -mt-2">
              {searchMatchCount === 0
                ? `Nenhuma unidade encontrada para "${unitSearch.trim()}".`
                : `${searchMatchCount} unidade${searchMatchCount !== 1 ? "s" : ""} na busca`}
            </p>
          )}

          {(filterValues.constructiveUnit ?? []).length > 0 && searchMatchCount === 0 && !unitSearch.trim() && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Nenhuma unidade com vínculo às unidades construtivas selecionadas. Use <strong>Vínculos UC</strong> para
              associar as casas ao orçamento.
            </p>
          )}

          {viewMode === "mapa" ? (
            appliedCostCenter.length !== 1 ? (
              <div className="text-center py-10 px-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50">
                <p className="text-slate-700 font-medium">Selecione exatamente uma obra</p>
                <p className="text-sm text-slate-500 mt-1">
                  O mapa de avanço físico (serviços × casas) é montado por centro de custo.
                </p>
              </div>
            ) : (
              <ChaoAvancoMapaPanel
                costCenterId={primaryCostCenterId}
                units={displayedUnits}
                canEditPlanejamento={isAdmin}
                canAtestar={can("chaoDeObraAprovar")}
                onProgressChanged={() => void reload()}
              />
            )
          ) : viewMode === "produtividade" ? (
            appliedCostCenter.length !== 1 ? (
              <div className="text-center py-10 px-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50">
                <p className="text-slate-700 font-medium">Selecione exatamente uma obra</p>
                <p className="text-sm text-slate-500 mt-1">
                  A produtividade compara o avanço físico semanal de um centro de custo.
                </p>
              </div>
            ) : (
              <ChaoProdutividadeView
                costCenterId={primaryCostCenterId}
                units={filteredUnits}
                unitLinksByCc={unitLinksByCc}
                constructiveUnitOptions={constructiveUnitOptions}
                canEdit={isAdmin || isMaster}
                initialConstructiveUnit={
                  (filterValues.constructiveUnit ?? []).length === 1
                    ? filterValues.constructiveUnit![0]
                    : undefined
                }
              />
            )
          ) : displayedUnits.length === 0 ? (
            <div className="text-center py-10 px-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50">
              <Home className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-700 font-medium">Nenhuma unidade na busca</p>
              <p className="text-sm text-slate-500 mt-1">Ajuste os filtros ou limpe o campo de busca.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {displayedUnits.map((u) => {
                const cc = String(u.costCenterId).trim();
                return (
                  <ChaoUnitProgressCard
                    key={u.id}
                    unit={u}
                    metrics={metrics[u.id]}
                    constructiveUnitLabel={resolveConstructiveUnitLabel(u, cc, unitLinksByCc)}
                    onOpen={() => setUnitTransitionLoading(true)}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <EapWeightsModal
          open={weightsOpen}
          onClose={() => setWeightsOpen(false)}
          catalog={weightsCatalog}
          onSaved={onWeightsSaved}
        />
      )}

      {primaryCostCenterId && (
        <>
          <ChaoUnitLinksModal
            open={linksModalOpen}
            onClose={() => setLinksModalOpen(false)}
            costCenterId={primaryCostCenterId}
            costCenterLabel={primaryCcLabel}
            units={filterUnitsByCostCenter(allUnits, [primaryCostCenterId])}
            constructiveUnitOptions={constructiveUnitOptions}
            onSaved={(links) => {
              setUnitLinksByCc((prev) => ({ ...prev, [primaryCostCenterId]: links }));
              setError(null);
            }}
          />
          <ChaoValorMedioUnidadeModal
            open={avgModalOpen}
            onClose={() => setAvgModalOpen(false)}
            costCenterId={primaryCostCenterId}
            costCenterLabel={primaryCcLabel}
            unitCount={unitCountByCc[primaryCostCenterId] ?? totalUnitsForObra}
            onSaved={(value) => {
              setUnitAvgByCc((prev) => ({ ...prev, [primaryCostCenterId]: value }));
              setError(null);
            }}
          />
          <ChaoDeducaoFinanceiraModal
            open={deductionModalOpen}
            onClose={() => setDeductionModalOpen(false)}
            costCenterId={primaryCostCenterId}
            costCenterLabel={primaryCcLabel}
            gerencialTotal={sumGerencialForCostCenters(
              gerencialOutcome ?? [],
              [primaryCostCenterId],
            )}
            onSaved={(value) => {
              setFinancialDeductionByCc((prev) => ({ ...prev, [primaryCostCenterId]: value }));
              setError(null);
            }}
          />
        </>
      )}

      <ChaoAvancoFisicoPorUcModal
        open={physicalByUcOpen}
        onClose={() => {
          setPhysicalByUcOpen(false);
          setScheduleRefreshKey((n) => n + 1);
        }}
        units={filteredUnits}
        metrics={metrics}
        appliedCostCenter={appliedCostCenter}
        unitLinksByCc={unitLinksByCc}
        obraGeneralPct={enterpriseGeneralPct}
      />
    </div>
  );
}
